import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  Heart,
  Cake,
  Sparkles,
  ChevronRight,
  ChevronDown
} from "lucide-react";
import Letterhead, {
  EXAMPLE_WEDDING_SPEECH,
  EXAMPLE_CONFIRMATION_SPEECH,
  EXAMPLE_BIRTHDAY_SPEECH
} from "@/components/Letterhead";

const ExamplesPage = () => {
  const [expandedSpeech, setExpandedSpeech] = useState(null);

  const speechExamples = [
    {
      id: "wedding",
      title: "Bryllupstale fra bedste ven",
      occasion: "Bryllup",
      icon: Heart,
      color: "bg-rose-500",
      speech: EXAMPLE_WEDDING_SPEECH,
      letterheadTitle: "Bryllupstale",
      letterheadSubtitle: "Til Anne & Thomas · Fra Mikkel",
      length: "Medium (~4 min)"
    },
    {
      id: "confirmation",
      title: "Konfirmationstale fra far",
      occasion: "Konfirmation",
      icon: Sparkles,
      color: "bg-amber-500",
      speech: EXAMPLE_CONFIRMATION_SPEECH,
      letterheadTitle: "Konfirmationstale",
      letterheadSubtitle: "Til Frederik · Fra Peter (far)",
      length: "Kort (~2 min)"
    },
    {
      id: "birthday",
      title: "60 års fødselsdagstale fra ægtefælle",
      occasion: "Rund fødselsdag",
      icon: Cake,
      color: "bg-emerald-500",
      speech: EXAMPLE_BIRTHDAY_SPEECH,
      letterheadTitle: "Fødselsdagstale",
      letterheadSubtitle: "Til Hanne · Fra Lars (ægtefælle)",
      length: "Medium (~4 min)"
    }
  ];

  return (
    <div className="min-h-screen bg-background py-8 md:py-16">
      <SEO
        title="Eksempler — Se rigtige AI-genererede festtaler | MinFesttale.dk"
        description="Se 3 komplette eksempler på AI-genererede festtaler: bryllupstale, konfirmationstale og fødselsdagstale. Se hvilke spørgsmål du skal besvare og hvordan den færdige tale ser ud."
        path="/eksempler"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-primary">
            Eksempler
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Se kvaliteten af vores AI-genererede taler. 
            Alle eksempler er baseret på fiktive personer.
          </p>
        </div>

        {/* Speech Examples */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="h-8 w-8 text-primary" />
            <h2 className="font-heading text-2xl font-bold text-primary">
              Eksempler på festtaler
            </h2>
          </div>

          <div className="space-y-8">
            {speechExamples.map((example) => (
              <div key={example.id} data-testid={`example-speech-${example.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${example.color} flex items-center justify-center`}>
                      <example.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-primary">
                        {example.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {example.occasion} · {example.length}
                      </p>
                    </div>
                  </div>
                </div>

                <Letterhead
                  title={example.letterheadTitle}
                  subtitle={example.letterheadSubtitle}
                  className={expandedSpeech === example.id ? "" : "max-h-[400px] overflow-hidden relative"}
                >
                  {example.speech}
                  {expandedSpeech !== example.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FFFEF9] to-transparent" />
                  )}
                </Letterhead>

                <Button
                  variant="outline"
                  onClick={() => setExpandedSpeech(expandedSpeech === example.id ? null : example.id)}
                  className="mt-4 w-full rounded-full"
                >
                  {expandedSpeech === example.id ? "Vis mindre" : "Læs hele talen"}
                  <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${expandedSpeech === example.id ? "rotate-180" : ""}`} />
                </Button>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/tale">
              <Button className="bg-primary hover:bg-primary/90 rounded-full px-8" data-testid="examples-tale-cta">
                Skriv din egen tale <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-muted/30 rounded-xl p-8">
          <h3 className="font-heading text-2xl font-bold text-primary mb-4">
            Klar til at skrive din egen?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Det tager kun 2 minutter at udfylde formularen, 
            og din personlige tale er klar med det samme.
          </p>
          <Link to="/tale">
            <Button className="bg-primary hover:bg-primary/90 rounded-full px-8">
              <FileText className="mr-2 h-4 w-4" />
              Skriv en festtale
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ExamplesPage;
